import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = () => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm'
          : 'bg-black/35 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            onClick={handleNavClick}
            className={`font-heading text-xl font-bold whitespace-nowrap transition-colors cursor-pointer ${
              scrolled ? 'text-foreground-900' : 'text-white'
            }`}
          >
            VigorBuy<span className="text-accent-500">Sheet</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-5">
            {/* Home */}
            <Link
              to="/"
              onClick={handleNavClick}
              className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                scrolled ? 'text-foreground-700 hover:text-foreground-900' : 'text-white/85 hover:text-white'
              }`}
            >
              Home
            </Link>

            {/* Shopping Guide */}
            <Link
              to="/article/vigorbuy-spreadsheet-guide-2026"
              onClick={handleNavClick}
              className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                scrolled ? 'text-foreground-700 hover:text-foreground-900' : 'text-white/85 hover:text-white'
              }`}
            >
              Shopping Guide
            </Link>

            {/* Category Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                <i className="ri-grid-line"></i>
                Categories
                <i className={`ri-arrow-down-s-line transition-transform text-xs ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-80 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-3 max-h-[420px] overflow-y-auto">
                  <p className="text-xs text-foreground-400 uppercase tracking-wider px-4 mb-2">Browse by Category</p>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={handleNavClick}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className={cat.icon}></i>
                      </span>
                      <span className="flex-1 whitespace-nowrap">{cat.name}</span>
                      <span className="text-xs text-foreground-400">{cat.count.toLocaleString()}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                scrolled ? 'text-foreground-700 hover:text-foreground-900' : 'text-white/85 hover:text-white'
              }`}
            >
              VigorBuy Sheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-accent-600 transition-all duration-300 cursor-pointer hover:scale-105"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors cursor-pointer ${
              scrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-white hover:bg-white/10'
            }`}
            aria-label="Toggle menu"
          >
            <i className={mobileMenuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 rounded-b-2xl border-t border-background-200 shadow-lg">
            {/* Quick Nav */}
            <div className="px-4 py-3 flex gap-2 border-b border-background-100">
              <Link
                to="/"
                onClick={handleNavClick}
                className="flex-1 text-center px-3 py-2.5 bg-background-100 text-foreground-700 rounded-lg text-sm font-medium whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-home-line mr-1"></i> Home
              </Link>
              <Link
                to="/article/vigorbuy-spreadsheet-guide-2026"
                onClick={handleNavClick}
                className="flex-1 text-center px-3 py-2.5 bg-background-100 text-foreground-700 rounded-lg text-sm font-medium whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-book-open-line mr-1"></i> Guide
              </Link>
            </div>

            {/* Categories */}
            <div className="px-2 py-3">
              <p className="text-xs text-foreground-400 uppercase tracking-wider px-3 mb-3 font-semibold">Browse Categories</p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={handleNavClick}
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className={`${cat.icon} text-xs`}></i>
                    </span>
                    <span className="truncate text-xs">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* CTAs */}
            <div className="px-4 pb-4 flex flex-col gap-2 pt-2 border-t border-background-100">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={handleNavClick}
                className="px-4 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-semibold text-center whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                VigorBuy Sheet <i className="ri-arrow-right-up-line ml-1"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={handleNavClick}
                className="px-4 py-3 bg-accent-500 text-background-50 rounded-xl text-sm font-semibold text-center whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-colors"
              >
                Buy It Now <i className="ri-shopping-cart-line ml-1"></i>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}